import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tent, Check, X as XIcon, EyeOff, MessageCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";

/**
 * BookingCheckPrompt — closes the affiliate-click → booking loop with
 * self-reported answers. Appears as a small bottom-anchored inline card
 * (NOT a blocking modal) when:
 *
 *   • user is logged in, AND
 *   • they made an affiliate click 48h–30d ago, AND
 *   • they haven't already answered or hidden the prompt
 *
 * Survives a session via the click_id — if the user dismisses ("hide")
 * it never returns for that click. If they choose Maybe / No / Yes the
 * answer is stored against the original click record so admin sees a
 * real click→booking conversion rate.
 *
 * Render this component once near the root (e.g. inside App.js layout).
 */
export default function BookingCheckPrompt() {
  const { user } = useAuth();
  const [pending, setPending] = useState(null);
  const [stage, setStage] = useState("ask"); // ask | yes-value | thanks
  const [value, setValue] = useState("");
  const [hidden, setHidden] = useState(false);
  const [daysAgo, setDaysAgo] = useState(1);

  useEffect(() => {
    if (!user) return;
    if ((user.role || "").toLowerCase() === "admin") return; // admins don't need the booking nudge
    let alive = true;
    api.get("/me/booking-checks/pending").then((r) => {
      if (!alive) return;
      const next = (r.data?.pending || [])[0];
      // Skip if user already dismissed in this session
      const dismissed = JSON.parse(sessionStorage.getItem("cc_bcp_dismissed") || "[]");
      if (next && !dismissed.includes(next.click_id)) {
        setPending(next);
        const createdAt = new Date(next.created_at).getTime();
        setDaysAgo(Math.max(1, Math.round((Date.now() - createdAt) / 86400000)));
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, [user]);

  const submit = async (answer, bookingValue) => {
    if (!pending) return;
    try {
      await api.post("/me/booking-check", {
        click_id: pending.click_id,
        answer,
        booking_value_gbp: bookingValue || null,
      });
    } catch (_) {
      // Non-fatal — UX continues regardless so the user isn't blocked
    }
    if (answer === "hide") {
      const dismissed = JSON.parse(sessionStorage.getItem("cc_bcp_dismissed") || "[]");
      dismissed.push(pending.click_id);
      sessionStorage.setItem("cc_bcp_dismissed", JSON.stringify(dismissed));
      setHidden(true);
    } else {
      setStage("thanks");
      setTimeout(() => setHidden(true), 2500);
    }
  };

  if (!user || !pending || hidden) return null;
  if ((user.role || "").toLowerCase() === "admin") return null;

  const siteName = pending.campsite_name || "the campsite";

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[400px] z-[55] bg-[#fffbf2] border-2 border-[#0e0e0e] shadow-[0_4px_0_0_#0e0e0e] animate-in slide-in-from-bottom-5 duration-300"
      data-testid="booking-check-prompt"
    >
      <button
        type="button"
        onClick={() => submit("hide")}
        aria-label="Hide this prompt"
        className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center text-[#666] hover:text-[#0e0e0e]"
        data-testid="booking-check-hide"
      >
        <EyeOff className="w-4 h-4" />
      </button>

      <div className="p-4 pr-9">
        {stage === "ask" && (
          <>
            <div className="flex items-center gap-2 text-[10px] mono uppercase tracking-[0.22em] text-[#ff4500] font-bold mb-2">
              <MessageCircle className="w-3 h-3" /> quick question
            </div>
            <div className="font-display text-lg leading-snug mb-1 text-[#0e0e0e]">
              You looked at <span className="font-bold">{siteName}</span> {daysAgo} day{daysAgo === 1 ? "" : "s"} ago.
            </div>
            <div className="text-sm text-[#555] mb-4">
              Did you end up booking it? Honest answer — it helps us champion the right sites.
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setStage("yes-value")}
                className="flex flex-col items-center gap-1 py-2.5 bg-[#16a34a] text-white hover:bg-[#0e0e0e] transition-colors text-xs font-bold uppercase tracking-wider"
                data-testid="booking-check-yes"
              >
                <Check className="w-4 h-4" /> Yes
              </button>
              <button
                type="button"
                onClick={() => submit("no")}
                className="flex flex-col items-center gap-1 py-2.5 bg-[#0e0e0e] text-white hover:bg-[#ff4500] transition-colors text-xs font-bold uppercase tracking-wider"
                data-testid="booking-check-no"
              >
                <XIcon className="w-4 h-4" /> No
              </button>
              <button
                type="button"
                onClick={() => submit("maybe")}
                className="flex flex-col items-center gap-1 py-2.5 bg-[#fbbf24] text-[#0e0e0e] hover:bg-[#0e0e0e] hover:text-white transition-colors text-xs font-bold uppercase tracking-wider"
                data-testid="booking-check-maybe"
              >
                <Tent className="w-4 h-4" /> Maybe
              </button>
            </div>
          </>
        )}

        {stage === "yes-value" && (
          <>
            <div className="flex items-center gap-2 text-[10px] mono uppercase tracking-[0.22em] text-[#16a34a] font-bold mb-2">
              <Check className="w-3 h-3" /> brilliant ·  one more thing
            </div>
            <div className="font-display text-lg leading-snug mb-1 text-[#0e0e0e]">
              Roughly what did the booking cost?
            </div>
            <div className="text-xs text-[#666] mb-3">
              Optional — leave blank to skip. Anonymous, never shared.
            </div>
            <div className="flex gap-2">
              <span className="flex items-center justify-center px-3 bg-[#0e0e0e] text-white font-display text-lg">£</span>
              <input
                type="number"
                step="1"
                min="0"
                placeholder="e.g. 85"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="flex-1 px-3 py-2 border-2 border-[#0e0e0e] focus:outline-none font-display text-lg"
                data-testid="booking-check-value"
              />
              <button
                type="button"
                onClick={() => submit("yes", value ? Number(value) : undefined)}
                className="px-4 py-2 bg-[#ff4500] text-white hover:bg-[#0e0e0e] transition-colors text-xs font-bold uppercase tracking-wider"
                data-testid="booking-check-yes-submit"
              >
                Done
              </button>
            </div>
            <button
              type="button"
              onClick={() => submit("yes")}
              className="mt-2 text-xs text-[#666] hover:text-[#0e0e0e] underline"
              data-testid="booking-check-skip-value"
            >
              Skip — just log &ldquo;yes&rdquo;
            </button>
          </>
        )}

        {stage === "thanks" && (
          <div className="py-2 text-center">
            <Check className="w-7 h-7 text-[#16a34a] mx-auto mb-2" strokeWidth={2.5} />
            <div className="font-display text-lg text-[#0e0e0e] mb-1">Thanks for the heads-up 🏕️</div>
            <div className="text-sm text-[#555]">Every honest answer makes the next camper&apos;s search a bit better.</div>
            <Link
              to="/wall"
              className="inline-block mt-3 text-xs mono uppercase tracking-[0.18em] text-[#ff4500] font-bold underline"
            >
              Share your trip on the wall →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
